import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ContactOfficeSection() {
  const { t } = useTranslation();
  const FAQS = Array.from({ length: 6 }, (_, i) => ({
    q: t(`contact.faq.items.${i}.q`),
    a: t(`contact.faq.items.${i}.a`),
  }));
  const OFFICES = [
    { city: t('contact.office.0.city'), flag: '🇸🇬', address: '160 Robinson Road, #14-04 SBF Center, Singapore 068914', phones: ['+44 7849923593', '+006589738114'], email: 'info@geelx.com', badge: 'HQ', color: '#FF2D78', showAddress: true, showEmail: true },
    { city: t('contact.office.1.city'), flag: '🇭🇰', address: '', phones: ['+85297903024'], email: 'hk@web3lab.sg', badge: 'BD', color: '#FF8C42', showAddress: false, showEmail: false },
    { city: t('contact.office.2.city'), flag: '🇨🇳', address: '', phones: [] as string[], email: '', badge: 'R&D', color: '#FF2D78', showAddress: false, showEmail: false },
  ];
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="bg-white py-20">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <span className="text-primary text-xs font-medium uppercase tracking-widest">{t('contact.offices.tag')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-gray-900 mb-4">{t('contact.offices.title')}</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">{t('contact.offices.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {OFFICES.map((office, i) => (
              <div key={i} className="relative p-6 bg-light-alt border border-primary/10 rounded-2xl hover:border-primary/25 transition-all duration-300">
                <div className="absolute top-5 right-5 px-2.5 py-0.5 text-xs font-bold rounded-full" style={{ background: `${office.color}12`, color: office.color, border: `1px solid ${office.color}25` }}>
                  {office.badge}
                </div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-2xl">{office.flag}</span>
                  <h3 className="text-base font-bold text-gray-800">{office.city}</h3>
                </div>
                <div className="space-y-3">
                  {office.showAddress && (
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-map-pin-line text-xs" style={{ color: office.color }} /></div>
                      <span className="text-sm text-gray-500 leading-relaxed">{office.address}</span>
                    </div>
                  )}
                  {office.phones.map((phone, j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-phone-line text-xs" style={{ color: office.color }} /></div>
                      <span className="text-sm text-gray-500">{phone}</span>
                    </div>
                  ))}
                  {office.showEmail && (
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-mail-line text-xs" style={{ color: office.color }} /></div>
                      <a href={`mailto:${office.email}`} className="text-sm text-gray-500 hover:text-primary transition-colors">{office.email}</a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-full">
              <span className="text-accent text-xs font-medium uppercase tracking-widest">{t('contact.faq.tag')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-gray-900 mb-5 leading-tight">
              {t('contact.faq.title')}
              <br />
              <span className="gradient-text">{t('contact.faq.highlight')}</span>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">{t('contact.faq.subtitle')}</p>
            <div className="p-5 bg-light-alt border border-primary/10 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                  <i className="ri-customer-service-2-line text-primary text-sm" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{t('contact.faq.notFound')}</div>
                  <div className="text-xs text-gray-400">{t('contact.faq.askExpert')}</div>
                </div>
              </div>
              <Link to="#form" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="block w-full text-center py-2.5 bg-primary/10 border border-primary/20 text-primary text-sm font-medium rounded-lg hover:bg-primary/20 transition-all cursor-pointer whitespace-nowrap">
                {t('contact.faq.submitForm')}
              </Link>
            </div>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`border rounded-xl transition-all duration-200 overflow-hidden ${openFaq === i ? 'border-primary/25 bg-light-alt' : 'border-primary/10 bg-white hover:border-primary/20'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer">
                  <span className={`text-sm font-medium ${openFaq === i ? 'text-primary' : 'text-gray-700'}`}>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${openFaq === i ? 'rotate-180 text-primary' : 'text-gray-300'}`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-gray-500 leading-relaxed border-t border-primary/10 pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 relative rounded-2xl overflow-hidden bg-light-alt border border-primary/10">
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-8 px-10 py-10">
            <div>
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">{t('contact.bottom.title')}</h3>
              <p className="text-gray-500 text-sm">{t('contact.bottom.subtitle')}</p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <a href="mailto:info@geelx.com" className="flex items-center gap-2 px-6 py-3 bg-white border border-primary/15 text-gray-600 text-sm font-medium rounded-lg hover:border-primary/30 hover:text-primary transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-mail-line" />{t('contact.bottom.emailUs')}
              </a>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary-dark transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-send-plane-fill" />{t('contact.bottom.submitNow')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
